// Turns an uploaded image into the small data URL sent to the API as the application logo (P5.15);
// the API uploads it to S3 or Spaces and keeps only the object key. Resizing in the browser keeps the request under the
// API's body limit and lets SVG uploads work even though the API only accepts raster images.

export const LOGO_SIZE = 128;
/** Mirrors MAX_LOGO_CHARS in packages/shared/src/schemas/apps.ts. */
export const MAX_LOGO_CHARS = 48_000;
export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif';

export async function logoFromFile(file: File): Promise<string> {
  if (!LOGO_ACCEPT.split(',').includes(file.type)) throw new Error('Choose a PNG, JPG, WebP, GIF or SVG image.');
  const src = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = src;
    await img.decode().catch(() => { throw new Error('That file could not be read as an image.'); });
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = LOGO_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot resize images.');
    // Fit the whole logo inside the square (no cropping); an SVG without a size reports 0.
    const w = img.naturalWidth || LOGO_SIZE;
    const h = img.naturalHeight || LOGO_SIZE;
    const scale = Math.min(LOGO_SIZE / w, LOGO_SIZE / h);
    ctx.drawImage(img, (LOGO_SIZE - w * scale) / 2, (LOGO_SIZE - h * scale) / 2, w * scale, h * scale);
    // WebP keeps transparency and is small; browsers without a WebP encoder return PNG instead.
    for (const quality of [0.9, 0.75, 0.6]) {
      const out = canvas.toDataURL('image/webp', quality);
      if (out.length <= MAX_LOGO_CHARS) return out;
    }
    throw new Error('That image is too detailed to use as a logo. Try a simpler one.');
  } finally {
    URL.revokeObjectURL(src);
  }
}
